/**
 * A person's colour, the same in every case and on every screen. The request of
 * 5 October 2026 (third), section 4.
 *
 * It is worked out from the user id and stored nowhere, so nobody chooses it,
 * nothing has to be migrated and two devices never disagree. The answer is a
 * slot number; the colours themselves are the `--user-N` tokens in
 * src/ui/tokens.css, and an element takes its slot through `data-user-color`.
 *
 * Eight slots, because more would need hues that read as a status (red, amber,
 * gold and green are taken). People therefore share a colour, which is why the
 * name is always printed beside it: the colour helps the eye, it never says who.
 */
export const USER_COLOR_COUNT = 8;

/**
 * The slot for a user id, 0 to USER_COLOR_COUNT - 1.
 *
 * FNV-1a over the id's code units, then the murmur3 finaliser. Plain FNV-1a
 * spreads short lower-case usernames badly once it is cut to three bits: it put
 * four of the lab's seventeen people on one colour and left a slot unused. The
 * finaliser mixes the high bits down, and the same seventeen then fill every
 * slot with at most three people on one.
 */
export function userColorIndex(userId: string): number {
  let hash = 0x811c9dc5;
  for (let index = 0; index < userId.length; index += 1) {
    hash ^= userId.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  hash ^= hash >>> 16;
  hash = Math.imul(hash, 0x85ebca6b);
  hash ^= hash >>> 13;
  hash = Math.imul(hash, 0xc2b2ae35);
  hash ^= hash >>> 16;
  return (hash >>> 0) % USER_COLOR_COUNT;
}
